import type {
  IEngagementBarSdPreset,
  IFontAwesomeIconsJson,
  IRemixIconsJson,
} from "@/shared/types";
import { environment } from "@environment";
import { withLocalCatalogElements } from "./local-catalog-elements";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export type TemplatesData = {
  categories?: { name: string; tags: string[] }[];
  templates?: {
    title: string;
    thumbnail: string;
    category: string;
    templateId: string;
    previewUrl: string;
    contentPointer: string;
  }[];
};

const KEEP_CACHE = 60 * 1000 * 60; // 60 minutes

export const staticAssetsApi = createApi({
  reducerPath: "staticAssets",
  baseQuery: fetchBaseQuery({
    baseUrl: "https://www.storydoc.com/assets",
  }),
  endpoints: (builder) => ({
    getElements: builder.query({
      query: () => "/json/elements.json",
      keepUnusedDataFor: KEEP_CACHE,
      // Every build: feature flags decide who sees a merged tile (see
      // local-catalog-elements.ts).
      transformResponse: (
        response: Parameters<typeof withLocalCatalogElements>[0],
      ) => withLocalCatalogElements(response),
    }),
    getLayouts: builder.query({
      query: () => "/json/layout-source.json",
      keepUnusedDataFor: KEEP_CACHE,
    }),
    getSubtypeCategories: builder.query({
      query: () => "/json/subtype-categories.json",
      keepUnusedDataFor: KEEP_CACHE,
    }),
    getSlidesLibrary: builder.query({
      query: () => "/json/slides-v2.json",
      keepUnusedDataFor: KEEP_CACHE,
      transformResponse: (response: Record<string, unknown>[]) => {
        return response.reduce(
          (acc: Record<string, Record<string, unknown>[]>, slide) => {
            const category = (slide.category || "Most popular") as string;
            if (!acc[category]) acc[category] = [];
            acc[category].push(slide);
            return acc;
          },
          {} as Record<string, Record<string, unknown>[]>,
        );
      },
    }),
    getRemixIcons: builder.query<IRemixIconsJson, any>({
      query: () => "/json/remixicon.json",
      keepUnusedDataFor: KEEP_CACHE,
    }),
    getFontAwesomeIcons: builder.query<IFontAwesomeIconsJson, any>({
      query: () => "/json/fontawesome.json",
      keepUnusedDataFor: KEEP_CACHE,
    }),
    getGoogleFonts: builder.query({
      query: () => "/json/google-fonts.json",
      keepUnusedDataFor: 0,
    }),
    getAccountIntegrations: builder.query({
      query: ({ id }: { id: string }) =>
        `https://${environment.assetsBucket}/integrations/${id}.json?v=${Date.now()}`,
    }),
    getMarketingIntegrations: builder.query({
      query: () => "/json/marketing-integrations.json",
      keepUnusedDataFor: KEEP_CACHE,
    }),
    getSubscriptionLimits: builder.query({
      query: () => "/json/subscription-limits.json",
      keepUnusedDataFor: KEEP_CACHE,
    }),
    getEngagementBarStorydocPresets: builder.query<
      IEngagementBarSdPreset[],
      unknown
    >({
      query: () => ({
        url: "/json/engagement-presets.json",
        method: "GET",
      }),
    }),
    getWorldMap: builder.query({
      query: () => "/json/world.json",
      keepUnusedDataFor: 0,
    }),
    getPlaceholderAnalytics: builder.query({
      query: () => "/json/analytics-placeholder.json",
      keepUnusedDataFor: 0,
    }),
    getTemplates: builder.query<TemplatesData, void>({
      query: () => "/json/templates-prod.min.json",
      // Kept for a while: the funnel's gallery mounts again on every Go
      // back, and refetching the whole feed each time left the user on a
      // skeleton for ten seconds before the cards they had just seen.
      keepUnusedDataFor: 600,
    }),
  }),
});

export const {
  useGetLayoutsQuery,
  useGetElementsQuery,
  useGetSubtypeCategoriesQuery,
  useGetSlidesLibraryQuery,
  useGetRemixIconsQuery,
  useGetFontAwesomeIconsQuery,
  useGetGoogleFontsQuery,
  useGetAccountIntegrationsQuery,
  useGetMarketingIntegrationsQuery,
  useGetSubscriptionLimitsQuery,
  useGetEngagementBarStorydocPresetsQuery,
  useGetWorldMapQuery,
  useGetPlaceholderAnalyticsQuery,
  useGetTemplatesQuery,
} = staticAssetsApi;
